import { CommonModule } from "@angular/common";
import {
  Component,
  AfterViewInit,
  OnDestroy,
  Input,
  ElementRef,
  forwardRef,
  EventEmitter,
  Output,
  OnInit,
} from "@angular/core";
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from "@angular/forms";
import * as $ from "jquery";
import "justifiedGallery";
import { SelectionIndicatorComponent } from "../platform/selection-indicator.component";
import { ButtonModule } from "primeng/button";
import { DialogModule } from "primeng/dialog";
import {
  ImageUploaderModule,
  AWSFileInformation,
  RemoteDataService,
} from "@nowinstore/nowinstore-core-ui/components";
import { User } from "@app/data/user";
import { USER_API_URL } from "@app/data/const";

@Component({
  imports: [
    CommonModule,
    SelectionIndicatorComponent,
    ButtonModule,
    DialogModule,
  ],
  standalone: true,
  selector: "nis-justified-gallery",
  templateUrl: "./justified-gallery.component.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => JustifiedGalleryComponent),
      multi: true,
    },
  ],
})
export class JustifiedGalleryComponent
  implements AfterViewInit, OnInit, OnDestroy, ControlValueAccessor
{
  _images: string[] = [];
  _selectedImage: string;

  private observer: MutationObserver;
  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};
  private disabled: boolean = false;
  preview?: string;
  showPreview: boolean = false;
  rowHeight: number = 150;
  directory: string = "";

  @Output() imageUploaded = new EventEmitter<AWSFileInformation>();

  constructor(
    private elRef: ElementRef,
    private remoteDataService: RemoteDataService
  ) {}

  async ngOnInit(): Promise<void> {
    const user = await this.remoteDataService.get<User>(USER_API_URL);
    var currentDate = new Date();
    this.directory =
      "/media/" +
      user._id +
      "/" +
      currentDate.getFullYear() +
      "/" +
      (currentDate.getMonth() + 1) +
      "/" +
      currentDate.getDate();
  }

  ngAfterViewInit(): void {
    // Initialize Justified Gallery once the view is fully loaded
    this.initGallery();

    // Observe changes to the gallery element
    this.observeGallery();
  }

  ngOnDestroy(): void {
    // Destroy the Justified Gallery instance when the component is destroyed
    this.destroyGallery();

    // Disconnect the observer
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  writeValue(value: any): void {
    this.selectedImage = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private initGallery(): void {
    const galleryElement = $(this.elRef.nativeElement).find(
      ".justified-gallery"
    );
    if (galleryElement.children().length === 0) {
      return; // Do not initialize if there are no children
    }

    this.destroyGallery(); // Ensure any existing gallery is destroyed before initializing

    (<any>galleryElement).justifiedGallery({
      rowHeight: this.rowHeight,
      margins: 5,
      lastRow: "nojustify",
      captions: false,
    });
  }

  set images(value: string[]) {
    this._images = value;
    this.initGallery();
  }
  @Input()
  get images(): string[] {
    return this._images;
  }

  set selectedImage(value: string) {
    this._selectedImage = value;
    this.onChange(value);
  }

  get selectedImage(): string {
    return this._selectedImage;
  }

  private destroyGallery(): void {
    const galleryElement = $(this.elRef.nativeElement).find(
      ".justified-gallery"
    );
    if (galleryElement.data("jg")) {
      (<any>galleryElement).justifiedGallery("destroy");
    }
  }

  private observeGallery(): void {
    const galleryElement =
      this.elRef.nativeElement.querySelector(".justified-gallery");

    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList") {
          this.initGallery();
        }
      });
    });

    this.observer.observe(galleryElement, {
      childList: true,
      subtree: true,
    });
  }

  onFileUploaded(image: AWSFileInformation) {
    this.selectedImage = image.url;
    this.imageUploaded.emit(image);
  }
}
